import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  CircularProgress,
  Collapse,
  Link as MuiLink,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { getPendingInviteToken, clearPendingInviteToken } from './InviteAccept';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    isAuthenticated,
    loginWithDevEmail,
    loginWithToken,
    acceptInvite,
    login,
  } = useAuth();

  const [error, setError] = useState('');
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [devEmail, setDevEmail] = useState('');
  const [devName, setDevName] = useState('');
  const [devLoading, setDevLoading] = useState(false);
  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');

  // If the user arrived via an invite link, the invite is redeemed server-side
  // during OAuth; finish the flow by confirming membership and landing on the
  // workspace picker (which auto-selects when there's a single workspace).
  const finishLogin = async () => {
    const pending = getPendingInviteToken();
    if (pending) {
      clearPendingInviteToken();
      await acceptInvite(pending);
      navigate('/org-picker', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  // Handle Google callback redirect: URL has ?token=... on success or ?error=... on failure
  useEffect(() => {
    const token = searchParams.get('token');
    const err = searchParams.get('error');
    const email = searchParams.get('email');
    if (err) {
      if (err === 'not_whitelisted') {
        setError(`${email || 'Your email'} is not whitelisted by any organization. Ask an org owner to invite you.`);
      } else {
        setError(`Sign-in failed: ${err}`);
      }
      // Clean URL
      window.history.replaceState({}, '', '/login');
      return;
    }
    if (token) {
      loginWithToken(token).then((res) => {
        if (res.success) {
          finishLogin();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated && !getPendingInviteToken()) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleGoogle = async () => {
    setError('');
    setLoadingGoogle(true);
    try {
      const r = await authAPI.getGoogleLoginUrl();
      window.location.href = r.data.auth_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Google sign-in unavailable. Use dev login or admin login below.');
      setLoadingGoogle(false);
    }
  };

  const handleDev = async (e) => {
    e.preventDefault();
    setError('');
    setDevLoading(true);
    const res = await loginWithDevEmail(devEmail.trim(), devName.trim() || undefined);
    if (!res.success) setError(res.error);
    else finishLogin();
    setDevLoading(false);
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setError('');
    const res = await login(adminUser, adminPass);
    if (res.success) {
      if (res.is_temporary_password) navigate('/change-password', { state: { forced: true } });
      else finishLogin();
    } else {
      setError(res.error);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f2ec', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ backgroundColor: '#00075e', py: 1.5, px: 4 }}>
        <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>
          Berkeley Technology &amp; Justice Lab
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', py: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 1 }}>
              PRA Machine
            </Typography>
            <Typography sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', color: '#838996', fontSize: '1rem' }}>
              Automated Public Records Act Requests
            </Typography>
          </Box>

          <Paper elevation={2} sx={{ p: 4, borderRadius: 2, backgroundColor: '#ffffff', border: '1px solid #e0ddd6' }}>
            <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 3 }}>
              Sign In
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleGoogle}
              startIcon={loadingGoogle ? <CircularProgress size={18} color="inherit" /> : <GoogleIcon />}
              disabled={loadingGoogle}
              sx={{
                py: 1.25,
                backgroundColor: '#2a52be',
                fontFamily: '"Helvetica Neue", Arial, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.95rem',
                '&:hover': { backgroundColor: '#203f99' },
              }}
            >
              {loadingGoogle ? 'Redirecting…' : 'Continue with Google'}
            </Button>

            <Box sx={{ my: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Divider sx={{ flex: 1 }} />
              <Typography variant="caption" sx={{ color: '#838996' }}>or</Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            <Box component="form" onSubmit={handleDev}>
              <Typography variant="caption" sx={{ color: '#838996', display: 'block', mb: 1 }}>
                Developer email login (enabled for local/docker builds)
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                required
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Full Name (optional)"
                value={devName}
                onChange={(e) => setDevName(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="outlined"
                disabled={devLoading || !devEmail}
                sx={{ textTransform: 'none' }}
              >
                {devLoading ? 'Signing in…' : 'Dev Sign In'}
              </Button>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <MuiLink
                component="button"
                type="button"
                onClick={() => setShowAdminForm((v) => !v)}
                sx={{ fontSize: '0.8rem', color: '#838996' }}
              >
                {showAdminForm ? 'Hide admin login' : 'Admin login (username + password)'}
              </MuiLink>
            </Box>

            <Collapse in={showAdminForm}>
              <Box component="form" onSubmit={handleAdminLogin} sx={{ mt: 2 }}>
                <TextField fullWidth size="small" label="Username" value={adminUser} onChange={(e) => setAdminUser(e.target.value)} sx={{ mb: 1 }} />
                <TextField fullWidth size="small" label="Password" type="password" value={adminPass} onChange={(e) => setAdminPass(e.target.value)} sx={{ mb: 1 }} />
                <Button type="submit" fullWidth variant="text" sx={{ textTransform: 'none' }}>Admin Sign In</Button>
              </Box>
            </Collapse>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
